/**
 * Signing in, from inside the editor.
 *
 * A visitor may design everything; saving, their list of designs and ordering
 * need an account. This is the one screen that says so. It is asked for by the
 * thing the visitor just tried to do, and it says what happens to their work -
 * because "sign in" with no word about the card on screen reads as "start
 * again", and a customer who thinks that leaves.
 *
 * Signing in happens on the shop's own My account page, with its own look and
 * its own registration form, and comes straight back here.
 */

import { useEffect, useRef, useState } from 'react';
import { getConfig } from '../api';
import { setPending, type Pending } from '../local';
import { currentDraftKey, keepLocally } from '../state/persist';
import { useEditor } from '../state/store';

const HEADINGS: Record<Pending, string> = {
  save: 'Sign in to save your design',
  basket: 'Sign in to order this design',
  designs: 'Sign in to see your designs',
  proof: 'Sign in to download a proof',
};

export function SignInDialog( { reason, onClose }: { reason: Pending; onClose: () => void } ) {
  const account = getConfig().account;
  const first = useRef<HTMLButtonElement>( null );

  useEffect( () => {
    first.current?.focus();

    const key = ( event: KeyboardEvent ) => {
      if ( event.key !== 'Escape' ) return;

      event.preventDefault();
      onClose();
    };

    window.addEventListener( 'keydown', key, true );

    return () => window.removeEventListener( 'keydown', key, true );
  }, [ onClose ] );

  if ( ! account ) return null;

  const go = () => {
    // Everything on screen goes into the browser first, and the reason with
    // it, so the editor that opens after signing in can finish the job.
    keepLocally();
    setPending( currentDraftKey(), reason );

    // Kept, so the "leave with unsaved changes?" question does not interrupt.
    useEditor.getState().markSaved();

    window.location.href = account.signIn;
  };

  return (
    <div className="tb-signin" role="dialog" aria-modal="true" aria-labelledby="tb-signin-title" onMouseDown={ ( event ) => event.target === event.currentTarget && onClose() }>
      <div className="tb-signin__card">
        <h2 id="tb-signin-title" className="tb-signin__title">{ HEADINGS[ reason ] }</h2>

        <p className="tb-signin__text">
          Your design is kept in this browser for now. Sign in and it moves into your account, where you can come back to it,
          change it and order it again. You do not need an account to order — only to keep a design for next time.
        </p>

        <div className="tb-signin__actions">
          <button ref={ first } type="button" className="tb-btn tb-btn--primary tb-signin__go" onClick={ go }>
            Sign in
          </button>

          { account.canRegister && (
            <button type="button" className="tb-btn tb-signin__new" onClick={ go }>
              Create an account
            </button>
          ) }

          <button type="button" className="tb-btn tb-btn--quiet" onClick={ onClose }>
            Not now
          </button>
        </div>

        {/*
          When the shop has sign-up switched off on My account there is no form
          to send a new customer to. Ordering does not need one, so the honest
          thing is to say so rather than leave them at a dead end.
        */}
        { ! account.canRegister && (
          <p className="tb-signin__note">
            No account yet? You can still order this design without one — contact us if you would like an account set up.
          </p>
        ) }
      </div>
    </div>
  );
}

/**
 * A design started in this browser before signing in, found on opening a new
 * card later - from an ordinary sign-in rather than the editor's own.
 *
 * Offered rather than taken: on a shared computer it may not be this
 * customer's card at all.
 */
export function LeftoverNotice( { onKeep, onDiscard, busy }: { onKeep: () => void; onDiscard: () => void; busy: boolean } ) {
  return (
    <div className="tb-toast tb-toast--offer" role="status">
      <span>You started a design on this card before signing in. Carry on with it?</span>
      <button type="button" className="tb-btn tb-btn--primary" disabled={ busy } onClick={ onKeep }>
        { busy ? 'Moving it…' : 'Carry on with it' }
      </button>
      <button type="button" className="tb-btn tb-btn--quiet" disabled={ busy } onClick={ onDiscard }>
        Discard it
      </button>
    </div>
  );
}

/** My designs, to a visitor: there is nothing to list until there is an account. */
export function SignedOutDesigns( { onSignIn, onClose }: { onSignIn: () => void; onClose: () => void } ) {
  return (
    <aside className="tb-side" aria-label="My designs">
      <div className="tb-side__head">
        <h2 className="tb-side__title">My designs</h2>
        <button type="button" className="tb-btn tb-btn--quiet" onClick={ onClose }>
          Close
        </button>
      </div>

      <div className="tb-side__empty tb-signin__inline">
        <p>Designs you save are kept in your account, so you can come back to them and order them again.</p>
        <button type="button" className="tb-btn tb-btn--primary" onClick={ onSignIn }>
          Sign in to see yours
        </button>
      </div>
    </aside>
  );
}

/**
 * What the shop asks before a design goes into the basket.
 *
 * A print shop prints what it is given, so the last thing between a typo and a
 * box of five hundred cards is the customer reading their own card. The wording
 * is the shop's; what they ticked, and when, goes onto the order.
 */
export function CheckDialog( { text, label, onConfirm, onClose }: { text: string; label: string; onConfirm: () => void; onClose: () => void } ) {
  const [ ticked, setTicked ] = useState( false );

  useEffect( () => {
    const key = ( event: KeyboardEvent ) => {
      if ( event.key !== 'Escape' ) return;

      event.preventDefault();
      onClose();
    };

    window.addEventListener( 'keydown', key, true );

    return () => window.removeEventListener( 'keydown', key, true );
  }, [ onClose ] );

  return (
    <div
      className="tb-signin"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tb-check-title"
      onMouseDown={ ( event ) => event.target === event.currentTarget && onClose() }
    >
      <div className="tb-signin__card">
        <h2 id="tb-check-title" className="tb-signin__title">Before it goes in the basket</h2>

        <p className="tb-signin__text">{ text }</p>

        <label className="tb-check">
          <input type="checkbox" checked={ ticked } onChange={ ( event ) => setTicked( event.target.checked ) } />
          <span>{ label }</span>
        </label>

        <div className="tb-signin__actions">
          <button type="button" className="tb-btn tb-btn--primary tb-check__go" disabled={ ! ticked } onClick={ onConfirm }>
            Add to basket
          </button>

          <button type="button" className="tb-btn tb-btn--quiet" onClick={ onClose }>
            Keep working on it
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What the shop is doing while a design goes into the basket.
 *
 * Adding to the basket is not one click and a new page: the design is sent to
 * the shop, a picture of it is made, the line is added and only then does the
 * basket appear. On a slow connection that is several seconds in which the
 * only sign anything happened was the word "Saving" in the corner - so
 * customers pressed it again, or left thinking it had not worked.
 *
 * It covers the screen on purpose. There is nothing useful to do while this
 * runs, and a second press would put a second line in the basket.
 */
export function BasketProgress( { step, updating = false }: { step: 'saving' | 'adding'; updating?: boolean } ) {
  return (
    <div className="tb-sending" role="alertdialog" aria-modal="true" aria-labelledby="tb-sending-title" aria-busy="true">
      <div className="tb-sending__card">
        <div className="tb-sending__spin" aria-hidden="true" />

        <h2 id="tb-sending-title" className="tb-sending__title">
          { 'saving' === step ? 'Saving your design…' : updating ? 'Updating your basket…' : 'Adding it to your basket…' }
        </h2>

        {/* Said once, plainly. The step above changes; this does not, so it
            does not flicker between the two. */}
        <p className="tb-sending__text">
          This takes a moment. Please do not close this window.
        </p>

        <ol className="tb-sending__steps">
          <li className={ `is-done` }>Design checked</li>
          <li className={ 'saving' === step ? 'is-now' : 'is-done' }>Sent to the shop</li>
          <li className={ 'adding' === step ? 'is-now' : '' }>{ updating ? 'Basket updated' : 'Added to your basket' }</li>
        </ol>
      </div>
    </div>
  );
}
